// @flow
import type { Node } from 'react';
import React, { PureComponent } from 'react';
import { Subscriber } from 'react-broadcast';
import isArray from 'lodash-es/isArray';
import isFunction from 'lodash-es/isFunction';
import mapValues from 'lodash-es/mapValues';

import { type ActionCreator, type EffectCreator } from '@rover/react-lib/lib/redux-duck';

import isShallowEqual from '../../../utils/isShallowEqual';

import { BROADCAST_CHANNEL } from './Provider';

export type Actions = { [string]: ActionCreator<*> };
export type Effects = { [string]: EffectCreator<*> };
/**
 * This `Orthogonal` type is a utility type to ensure two
 * objects do not have the same keys. This helps static
 * analysis when creating a combined object type.
 */
type Orthogonal<A, B> = $Diff<A, $Exact<$ObjMap<B, () => *>>>;

const getActions = (actions: Actions, dispatch: any, { prevActions, prevDispatchActions }) =>
  mapValues(actions, (action, name) => {
    if (!action || !isFunction(action)) {
      throw new Error(`Connect action values must be functions (action ${name})`);
    }

    return action === prevActions[name]
      ? prevDispatchActions[name]
      : (...args) => dispatch(action(...args));
  });

export type Props<A: Actions, E: Effects, S: { [string]: any }> = {
  selector: (any, any) => S,
  actions: A,
  effects: E,
  /**
   * The `children` type here is an object that combines 3 things:
   * 1. actions and effects that have been wrapped in `dispatch` (and must not contain the same keys)
   * 2. the selector functions return type
   * 3. the dispatch function itself
   *
   * The $ObjMap-ing takes a function of ActionCreator or EffectCreator type
   * and changes the return type to be more inline with that of the `dispatch` function.
   *
   * i.e. from `P => { type: string, payload: P }` to `P => void`
   */
  children: ({
    ...$Exact<$ObjMap<Orthogonal<E, A>, <P>(EffectCreator<P>) => (P) => any>>,
    ...$Exact<$ObjMap<Orthogonal<A, E>, <P>(ActionCreator<P>) => (P) => void>>,
    ...$Exact<S>,
    dispatch: Function,
  }) => Node,
  store?: Object,
  onMount?: Function | Array<Function>,
  onUnmount?: Function | Array<Function>,
};

export type State = {
  selectorProps: Object,
  actionProps: Object,
  effectProps: Object,
  dispatch?: Function,
};

type InnerConnectProps<A, E, S> = Props<A, E, S> & {
  reduxStore: any,
};

class InnerConnect extends PureComponent<InnerConnectProps<*, *, *>, State> {
  unsubscribe: Function;

  static defaultProps = {
    selector: () => ({}),
    actions: {},
    effects: {},
    store: undefined,
    onMount: undefined,
    onUnmount: undefined,
  };

  state: State = {
    selectorProps: {},
    actionProps: {},
    effectProps: {},
  };

  // eslint-disable-next-line react/sort-comp
  prevActions = {};

  prevEffects = {};

  prevDispatchActions = {};

  prevDispatchEffects = {};

  componentWillMount() {
    const { store } = this;
    const { onMount } = this.props;

    if (onMount) {
      if (isArray(onMount)) {
        onMount.forEach((action) => {
          store.dispatch(action());
        });
      } else {
        // $FlowFixMe
        store.dispatch(onMount());
      }
    }

    this.unsubscribe = store.subscribe(this.updateState);
    this.updateState();

    const { selector } = this.props;
    const nextSelectorProps = selector(store.getState() || {}, this.props);
    this.setState(() => ({
      selectorProps: nextSelectorProps,
      actionProps: this.getActions(),
      effectProps: this.getEffects(),
      dispatch: store.dispatch,
    }));
  }

  componentWillUnmount() {
    const { store } = this;
    this.unsubscribe();
    this.unmounted = true;

    const { onUnmount } = this.props;
    if (onUnmount) {
      if (isArray(onUnmount)) {
        onUnmount.forEach((action) => {
          store.dispatch(action());
        });
      } else {
        // $FlowFixMe
        store.dispatch(onUnmount());
      }
    }
  }

  unmounted = false;

  get store() {
    return this.props.store || this.props.reduxStore;
  }

  getActions = () => {
    const { store } = this;

    const dispatchActions = getActions(this.props.actions, store.dispatch, {
      prevActions: this.prevActions,
      prevDispatchActions: this.prevDispatchActions,
    });

    this.prevActions = this.props.actions;
    this.prevDispatchActions = dispatchActions;
    return dispatchActions;
  };

  getEffects = () => {
    const { store } = this;

    const dispatchEffects = getActions(this.props.effects, store.dispatch, {
      prevActions: this.prevEffects,
      prevDispatchActions: this.prevDispatchEffects,
    });

    this.prevEffects = this.props.effects;
    this.prevDispatchEffects = dispatchEffects;
    return dispatchEffects;
  };

  updateState = () => {
    const { store } = this;
    const { selector } = this.props;
    const nextSelectorProps = selector(store.getState() || {}, this.props);
    if (!isShallowEqual(nextSelectorProps, this.state.selectorProps)) {
      // NOTE: In a nested form, if there is an action that causes FormA to unmount FormB,
      // FormB's reducer will be cleared before the update arrive. Since unsubscribe does not
      // affect actions that are already in progress, this function will run even unmounted.
      if (this.unmounted) {
        return;
      }

      this.setState(() => ({
        // We recompute the selector props here rather than use nextSelectorProps since the value
        // may change while this running and cause selectors/props to be out of sync.
        selectorProps: selector(store.getState() || {}, this.props),
        actionProps: this.getActions(),
        effectProps: this.getEffects(),
        dispatch: store.dispatch,
      }));
    }
  };

  render() {
    return this.props.children({
      ...this.state.selectorProps,
      ...this.state.actionProps,
      ...this.state.effectProps,
      dispatch: this.state.dispatch,
    });
  }
}

const Connect = <A, E, S>(props: Props<A, E, S>) => (
  <Subscriber channel={BROADCAST_CHANNEL}>
    {(context) => <InnerConnect {...context} {...props} />}
  </Subscriber>
);

Connect.defaultProps = {
  selector: () => ({}: {}),
  actions: {},
  effects: {},
  store: undefined,
  onMount: undefined,
  onUnmount: undefined,
};

export default Connect;



// WEBPACK FOOTER //
// ../react-lib/src/components/utils/Redux/Connect.jsx